/**
 * Main App Component
 * Sets up routing and main layout structure
 */

import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import safeInternalRedirect from './utils/safeRedirect';
import sessionCheckResult from './utils/sessionCheck';
import { Navbar, Footer } from './components/layout';
import { Home, LandingPage, Login, RequestAccess, CheckRequestStatus, ChangePassword, CompleteProfile, AccessManagement, ResellerAssets, UserGuides } from './pages';
import { 
  CalibrationTool, 
  EStepsCalibration, 
  SalesCommission,
  ResellerDashboard,
  MachineGateway,
  CTECalculator,
  CTEEmbed,
  PartAlignmentEmbed
} from './pages/tools';
import {
  DesignCheckHome,
  AnalysisPage as DesignCheckAnalysis,
  GuidePage as DesignCheckGuide,
  ChatPage as DesignCheckChat,
  InviteLanding as DesignCheckInvite,
  InvitesPage as DesignCheckInvites,
  DesignCheckGoodbye
} from './pages/design-check';
import { ChatbotWidget } from './components/chatbot';
import SupportPage from './pages/support/SupportPage';
import SupportInsights from './pages/support/SupportInsights';
import SupportSettings from './pages/support/SupportSettings';
import { isInternalStaff } from './config/navMenu';
import './styles/global.css';
import './styles/components.css';

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    if (window.location.hash) return;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.querySelectorAll('main, .rf-main, .rf-portal-content').forEach((el) => {
      el.scrollTop = 0;
    });
  }, [pathname, search]);
  return null;
}

function SignedInRedirect() {
  const [searchParams] = useSearchParams();
  return <Navigate to={safeInternalRedirect(searchParams.get('redirect'))} replace />;
}

/**
 * Hides portal chrome (Navbar/Footer/Chatbot) on embed and Design Check
 * routes. Must live inside the Router so it reacts to client-side
 * navigation — reading window.location once in App broke this.
 */
function PortalChrome({ children }) {
  const { pathname } = useLocation();
  const hide = pathname === '/cte-embed' || pathname.startsWith('/design-check');
  return hide ? null : <>{children}</>;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [earlyAccess, setEarlyAccess] = useState(false);
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [requireProfileComplete, setRequireProfileComplete] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    const user = localStorage.getItem('currentUser');
    const role = localStorage.getItem('userRole');
    const storedEarlyAccess = localStorage.getItem('earlyAccess') === 'true';
    const passwordChangeRequired = localStorage.getItem('requirePasswordChange') === 'true';
    const profileCompleteRequired = localStorage.getItem('requireProfileComplete') === 'true';

    if (token && tokenExpiry) {
      const now = Date.now();
      if (parseInt(tokenExpiry) > now) {
        setIsAuthenticated(true);
        setCurrentUser(user);
        setUserRole(role);
        setEarlyAccess(storedEarlyAccess);
        setRequirePasswordChange(passwordChangeRequired);
        setRequireProfileComplete(profileCompleteRequired);
        verifyAuth(token).finally(() => setAuthLoading(false));
      } else {
        handleLogout();
        setAuthLoading(false);
      }
    } else {
      setAuthLoading(false);
    }
  }, []);

  const verifyAuth = async (token) => {
    try {
      const response = await fetch('/api/auth/verify', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        data = null;
      }
      // A dead token must clear the saved sign-in. Leaving it in place
      // bounces the user straight back to the page that just rejected it.
      if (sessionCheckResult(response.status, data) === 'logout') {
        handleLogout();
        return;
      }
      if (!response.ok || !data || !data.success) {
        return;
      }

      setUserRole(data.role);
      localStorage.setItem('userRole', data.role);
      if (data.earlyAccess !== undefined) {
        setEarlyAccess(!!data.earlyAccess);
        localStorage.setItem('earlyAccess', data.earlyAccess ? 'true' : 'false');
      }
      if (data.requireProfileComplete !== undefined) {
        setRequireProfileComplete(data.requireProfileComplete);
        localStorage.setItem('requireProfileComplete', data.requireProfileComplete ? 'true' : 'false');
      }
    } catch (error) {
      console.error('Auth verification failed:', error);
    }
  };

  const handleLogin = (data) => {
    setIsAuthenticated(true);
    setCurrentUser(data.username);
    // Use role from login response directly (avoids race condition)
    if (data.role) {
      setUserRole(data.role);
      localStorage.setItem('userRole', data.role);
    }
    if (data.earlyAccess !== undefined) {
      setEarlyAccess(!!data.earlyAccess);
      localStorage.setItem('earlyAccess', data.earlyAccess ? 'true' : 'false');
    }
    // Check if password change is required
    if (data.requirePasswordChange) {
      setRequirePasswordChange(true);
      localStorage.setItem('requirePasswordChange', 'true');
    }
    // Check if profile completion is required
    if (data.requireProfileComplete) {
      setRequireProfileComplete(true);
      localStorage.setItem('requireProfileComplete', 'true');
    }
  };

  const handlePasswordChanged = () => {
    setRequirePasswordChange(false);
    localStorage.removeItem('requirePasswordChange');
  };

  const handleProfileComplete = () => {
    setRequireProfileComplete(false);
    localStorage.removeItem('requireProfileComplete');
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userRole');
    localStorage.removeItem('earlyAccess');
    localStorage.removeItem('requirePasswordChange');
    localStorage.removeItem('requireProfileComplete');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setUserRole(null);
    setEarlyAccess(false);
    setRequirePasswordChange(false);
    setRequireProfileComplete(false);
  };

  // Protected Route wrapper
  const ProtectedRoute = ({ children }) => {
    if (authLoading) return null;
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    if (userRole === 'client') {
      // Design Check invitees never see the portal
      return <Navigate to="/design-check" replace />;
    }
    if (requirePasswordChange) {
      return <Navigate to="/change-password" replace />;
    }
    if (requireProfileComplete) {
      return <Navigate to="/complete-profile" replace />;
    }
    return children;
  };

  const InternalStaffGate = ({ children }) => {
    const allowed = isInternalStaff(userRole);
    useEffect(() => {
      if (authLoading || allowed) return;
      if (window.history.length > 1) window.history.back();
      else window.location.replace('/');
    }, [allowed]);
    if (!allowed) return null;
    return children;
  };

  // Design Check route wrapper: any authenticated user (clients included)
  const DesignCheckRoute = ({ children }) => {
    if (authLoading) return null;
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  // Full-page redirect to static HTML served by Express (e.g. sales-pipeline.html).
  // Keep the current query string so /field-ops-manual.html?set=apollo still
  // arrives on the static page with set/course/id intact.
  const RedirectToStatic = ({ url }) => {
    const { search } = useLocation();
    useEffect(() => {
      const isDev = window.location.port === '3000';
      const token = localStorage.getItem('authToken');
      const dest = new URL(url, window.location.origin);
      new URLSearchParams(search).forEach((value, key) => {
        if (key === 'token') return;
        if (!dest.searchParams.has(key)) dest.searchParams.set(key, value);
      });
      if (token) dest.searchParams.set('token', token);
      const target = dest.pathname + dest.search;
      window.location.replace(isDev ? 'http://localhost:3001' + target : target);
    }, [url, search]);
    return null;
  };


  return (
    <Router>
      <ScrollToTop />
      <div className="rf-app">
        <PortalChrome>
          <Navbar
            isAuthenticated={isAuthenticated}
            currentUser={currentUser}
            userRole={userRole}
            onLogout={handleLogout}
          />
        </PortalChrome>

        <main className="rf-main">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                authLoading ? null :
                isAuthenticated ? (
                  <SignedInRedirect />
                ) : (
                  <Login onLogin={handleLogin} />
                )
              }
            />

            {/* Request Access - Public Route */}
            <Route
              path="/request-access"
              element={
                isAuthenticated ? (
                  <Navigate to="/" replace />
                ) : (
                  <RequestAccess />
                )
              }
            />

            {/* Check Request Status - Public Route */}
            <Route
              path="/check-status"
              element={
                isAuthenticated ? (
                  <Navigate to="/" replace />
                ) : (
                  <CheckRequestStatus />
                )
              }
            />

            {/* Change Password - Protected but special handling */}
            <Route
              path="/change-password"
              element={
                isAuthenticated ? (
                  <ChangePassword
                    isRequired={requirePasswordChange}
                    onPasswordChanged={handlePasswordChanged}
                  />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Complete Profile - Protected but special handling */}
            <Route
              path="/complete-profile"
              element={
                isAuthenticated ? (
                  <CompleteProfile onProfileComplete={handleProfileComplete} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Home - Landing page for visitors, Dashboard for authenticated users */}
            <Route
              path="/"
              element={
                authLoading ? null :
                isAuthenticated ? (
                  userRole === 'client' ? (
                    <Navigate to="/design-check" replace />
                  ) : requirePasswordChange ? (
                    <Navigate to="/change-password" replace />
                  ) : requireProfileComplete ? (
                    <Navigate to="/complete-profile" replace />
                  ) : (
                    <Home userRole={userRole} earlyAccess={earlyAccess} />
                  )
                ) : (
                  <LandingPage />
                )
              }
            />

            {/* Dashboard - Protected shortcut */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Home userRole={userRole} earlyAccess={earlyAccess} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reseller-assets"
              element={
                <ProtectedRoute>
                  <ResellerAssets />
                </ProtectedRoute>
              }
            />

            <Route
              path="/guides"
              element={
                <ProtectedRoute>
                  <UserGuides userRole={userRole} earlyAccess={earlyAccess} />
                </ProtectedRoute>
              }
            />

            {/* Internal staff: user list and add-user. Admin-only tabs stay gated in the page. */}
            <Route
              path="/admin/access"
              element={
                <ProtectedRoute>
                  <InternalStaffGate>
                    <AccessManagement userRole={userRole} />
                  </InternalStaffGate>
                </ProtectedRoute>
              }
            />

            {/* Tool Routes */}
            <Route
              path="/calibration"
              element={
                <ProtectedRoute>
                  <CalibrationTool />
                </ProtectedRoute>
              }
            />

            <Route
              path="/esteps"
              element={
                <ProtectedRoute>
                  <EStepsCalibration />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cost-calculator"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/cost-calculator" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/roi"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/cost-calculator" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/commission"
              element={
                <ProtectedRoute>
                  <SalesCommission />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reseller-dashboard"
              element={
                <ProtectedRoute>
                  <ResellerDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/gateway"
              element={
                <ProtectedRoute>
                  <MachineGateway />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cte-calculator"
              element={
                <ProtectedRoute>
                  <CTECalculator />
                </ProtectedRoute>
              }
            />

            {/* Retired Calibration Training HTML → online course */}
            <Route
              path="/training/calibration"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-course.html?id=pe320-calibration" />
                </ProtectedRoute>
              }
            />

            {/* Static HTML redirects (full page load so static page renders) */}
            <Route
              path="/quote-to-production"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/quote-to-production" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/global-reach"
              element={
                <ProtectedRoute>
                  <InternalStaffGate>
                    <RedirectToStatic url="/global-reach" />
                  </InternalStaffGate>
                </ProtectedRoute>
              }
            />
            {/* Old name for Quote to Production */}
            <Route
              path="/sales-pipeline"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/quote-to-production" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/field-ops"
              element={
                <ProtectedRoute>
                  <InternalStaffGate>
                    <RedirectToStatic url="/field-ops" />
                  </InternalStaffGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-hs-library"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops/hs-library" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-sop-library"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops/sop-library" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-evo3d-install"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/form-evo3d-install.html" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-rf-project"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-project" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-admin"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-admin.html" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-visitor-log"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/visitor-log" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/internal-jobs"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/internal-jobs" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/field-ops-courses.html"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-courses.html" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-manual-review"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-manual-review" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-manual-review.html"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-manual-review" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-manual.html"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-manual.html" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-manual-pdf.html"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-manual-pdf.html" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-course.html"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-course.html" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/document-store"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/document-store" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/erp" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/oauth/resume"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/oauth/resume" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bugs-features"
              element={
                <ProtectedRoute>
                  <InternalStaffGate>
                    <RedirectToStatic url="/bugs-features" />
                  </InternalStaffGate>
                </ProtectedRoute>
              }
            />

            <Route
              path="/install-scheduler"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/install-scheduler" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-training-delivery"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/field-ops-training-delivery" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-ops-records"
              element={
                <ProtectedRoute>
                  <InternalStaffGate>
                    <RedirectToStatic url="/field-ops-records.html" />
                  </InternalStaffGate>
                </ProtectedRoute>
              }
            />

            <Route
              path="/tech-support"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/tech-support" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/price-list"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/price-list" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support/insights"
              element={
                <ProtectedRoute>
                  <InternalStaffGate>
                    <SupportInsights />
                  </InternalStaffGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/support/settings"
              element={
                <ProtectedRoute>
                  <InternalStaffGate>
                    <SupportSettings userRole={userRole} />
                  </InternalStaffGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportPage userRole={userRole} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/linear-chatbot"
              element={
                <ProtectedRoute>
                  <RedirectToStatic url="/linear-chatbot/" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/part-alignment"
              element={
                <ProtectedRoute>
                  <PartAlignmentEmbed />
                </ProtectedRoute>
              }
            />

            {/* CTE Embed - no auth, no layout (loaded in iframe) */}
            <Route path="/cte-embed" element={<CTEEmbed />} />

            {/* Design Check — client-facing STEP DFM advisor */}
            <Route path="/design-check/invite/:token" element={<DesignCheckInvite />} />
            <Route path="/design-check/goodbye" element={<DesignCheckGoodbye />} />
            <Route
              path="/design-check"
              element={
                <DesignCheckRoute>
                  <DesignCheckHome />
                </DesignCheckRoute>
              }
            />
            <Route
              path="/design-check/analysis/:jobId"
              element={
                <DesignCheckRoute>
                  <DesignCheckAnalysis />
                </DesignCheckRoute>
              }
            />
            <Route
              path="/design-check/guide"
              element={
                <DesignCheckRoute>
                  <DesignCheckGuide />
                </DesignCheckRoute>
              }
            />
            <Route
              path="/design-check/guide/:sectionId"
              element={
                <DesignCheckRoute>
                  <DesignCheckGuide />
                </DesignCheckRoute>
              }
            />
            <Route
              path="/design-check/chat"
              element={
                <DesignCheckRoute>
                  <DesignCheckChat />
                </DesignCheckRoute>
              }
            />
            <Route
              path="/design-check/invites"
              element={
                <DesignCheckRoute>
                  <DesignCheckInvites />
                </DesignCheckRoute>
              }
            />

            {/* Catch all - redirect to home */}
            <Route
              path="*"
              element={<Navigate to={userRole === 'client' ? '/design-check' : '/'} replace />}
            />
          </Routes>
        </main>

        <PortalChrome>
          <Footer />
          {isAuthenticated && <ChatbotWidget />}
        </PortalChrome>
      </div>
    </Router>
  );
}

export default App;
